import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { concertsAPI } from '../api/endpoints';
import { upcomingDateAfter } from '../utils/concertDates';
import type { ConcertSearchParams } from '../types';

const DEFAULT_PAGE_SIZE = 100;

/**
 * The statuses shown by default: the concerts still live. Canceled ones stay on file but are not
 * what the page opens on. Comma-separated in the order the status filter lists them, so the
 * string can be compared against the URL as-is.
 */
export const DEFAULT_STATUS_FILTER = 'in_discussion,done';

export function useConcertSearch() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Arriving on the page with nothing in the URL shows the concerts that are still live and still
  // ahead, rather than every concert ever played. These are defaults, not locked filters: the
  // first param the user writes (including clearing a filter, or "Reset to All") hands the URL
  // full authority, so an emptied URL is then taken at face value instead of defaulting again.
  const [defaultsApply, setDefaultsApply] = useState(true);
  const useDefaults = defaultsApply && Array.from(searchParams.keys()).length === 0;

  // Keep the defaults visible in the URL, so a reload or a shared link reproduces what is shown.
  useEffect(() => {
    if (!useDefaults) return;
    const next = new URLSearchParams();
    next.set('date_after', upcomingDateAfter());
    next.set('status', DEFAULT_STATUS_FILTER);
    setSearchParams(next, { replace: true });
  }, [useDefaults, setSearchParams]);

  // Parse URL params
  const params: ConcertSearchParams = {
    q: searchParams.get('q') || undefined,
    status: (useDefaults ? DEFAULT_STATUS_FILTER : searchParams.get('status')) || undefined,
    band_id: searchParams.get('band_id') || undefined,
    venue_id: searchParams.get('venue_id') || undefined,
    date_after: (useDefaults ? upcomingDateAfter() : searchParams.get('date_after')) || undefined,
    date_before: searchParams.get('date_before') || undefined,
    checklist: searchParams.get('checklist') || undefined,
    sort_by: searchParams.get('sort_by') || undefined,
    sort_order: (searchParams.get('sort_order') as 'asc' | 'desc') || undefined,
    page: Number(searchParams.get('page')) || 1,
    page_size: Number(searchParams.get('page_size')) || DEFAULT_PAGE_SIZE,
  };

  // Query
  const query = useQuery({
    queryKey: ['concerts', params],
    queryFn: async ({ signal }) => {
      const response = await concertsAPI.list(params, signal);
      return response.data;
    },
    placeholderData: (previousData) => previousData,
    staleTime: 30000,
  });

  // Update URL params helper
  const updateParams = (updates: Partial<ConcertSearchParams>) => {
    setDefaultsApply(false);
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined || value === '' || value === null) {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    });
    setSearchParams(newParams);
  };

  // Convenience methods
  const setSearch = (q: string) => updateParams({ q: q || undefined, page: 1 });
  const setFilter = (key: string, value: string | undefined) =>
    updateParams({ [key]: value, page: 1 });
  const setFilters = (filters: Record<string, string | undefined>) =>
    updateParams({ ...filters, page: 1 });
  const setSort = (sort_by: string, sort_order: 'asc' | 'desc' | null) =>
    updateParams({ sort_by: sort_order ? sort_by : undefined, sort_order: sort_order ?? undefined });
  const setPage = (page: number) => updateParams({ page });
  const resetFilters = () => {
    setDefaultsApply(false);
    setSearchParams(new URLSearchParams());
  };

  const setAllParams = (newParams: Record<string, string | undefined>) => {
    setDefaultsApply(false);
    const urlParams = new URLSearchParams();
    Object.entries(newParams).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        urlParams.set(key, value);
      }
    });
    setSearchParams(urlParams);
  };

  return {
    concerts: query.data?.items ?? [],
    pagination: query.data
      ? {
          total: query.data.total,
          page: query.data.page,
          pageSize: query.data.page_size,
          totalPages: query.data.total_pages,
          hasNext: query.data.has_next,
          hasPrev: query.data.has_prev,
        }
      : null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    params,
    setSearch,
    setFilter,
    setFilters,
    setSort,
    setPage,
    resetFilters,
    setAllParams,
    refetch: query.refetch,
  };
}
